// Shared presentational bits for the Repo Governance screens.
//
// Severity is the primary signal on every one of these pages, so it gets one
// definition here rather than a slightly different colour per screen.

import { Bot, ShieldCheck } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import type {
  FindingSource,
  GovernanceSeverity,
  GovernanceVerdict,
  ReviewStatus,
} from "@/types/api";

const SEVERITY_CLASS: Record<GovernanceSeverity, string> = {
  critical: "border-transparent bg-destructive text-destructive-foreground",
  high: "border-transparent bg-[hsl(var(--warning))] text-white",
  medium: "border-transparent bg-[hsl(var(--warning))]/70 text-white",
  low: "border-transparent bg-muted text-muted-foreground",
  info: "border-transparent bg-muted text-muted-foreground",
};

export function SeverityBadge({
  severity,
  className,
}: {
  severity: GovernanceSeverity | null;
  className?: string;
}) {
  if (!severity) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <Badge className={cn(SEVERITY_CLASS[severity], "uppercase tracking-wide", className)}>
      {severity}
    </Badge>
  );
}

const VERDICT_LABEL: Record<GovernanceVerdict, string> = {
  pass: "Clean",
  warn: "Needs a look",
  fail: "Risky",
};

export function VerdictBadge({
  verdict,
  status,
}: {
  verdict: GovernanceVerdict | null;
  status: ReviewStatus;
}) {
  if (status === "queued") return <Badge variant="secondary">Queued</Badge>;
  if (status === "analyzing") return <Badge variant="secondary">Analysing…</Badge>;
  if (status === "failed") return <Badge variant="outline">Scan failed</Badge>;
  if (!verdict) return <Badge variant="secondary">—</Badge>;
  return (
    <Badge
      variant={verdict === "fail" ? "destructive" : verdict === "warn" ? "warning" : "success"}
    >
      {VERDICT_LABEL[verdict]}
    </Badge>
  );
}

/**
 * Distinguishes a deterministic rule hit from the model's judgement.
 *
 * Not decoration: a rule finding cannot be talked out of firing by anything in
 * the analysed diff, so a reviewer triaging a queue should know which of the
 * two they are reading before they decide how much to trust it.
 */
export function SourceBadge({ source }: { source: FindingSource }) {
  return source === "rule" ? (
    <span
      className="inline-flex items-center gap-1 text-xs text-muted-foreground"
      title="Deterministic rule — cannot be suppressed by the content it analysed."
    >
      <ShieldCheck className="h-3 w-3" /> rule
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 text-xs text-muted-foreground"
      title="AI analysis — judgement, not a guarantee. Verify before acting."
    >
      <Bot className="h-3 w-3" /> AI
    </span>
  );
}

export function CategoryLabel({ category }: { category: string }) {
  return <span className="text-xs text-muted-foreground">{category.replace(/_/g, " ")}</span>;
}

export function Actor({ login }: { login: string | null }) {
  if (!login) return <span className="text-xs text-muted-foreground">unknown</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      {/* GitHub's avatar endpoint is public and needs no token. */}
      <img
        src={`https://github.com/${encodeURIComponent(login)}.png?size=40`}
        alt=""
        className="h-5 w-5 rounded-full bg-muted"
        loading="lazy"
      />
      <span className="text-sm">@{login}</span>
    </span>
  );
}

export function Sha({ sha }: { sha: string | null }) {
  if (!sha) return null;
  return <code className="rounded bg-muted px-1 py-0.5 text-xs">{sha.slice(0, 7)}</code>;
}

/** Relative time, falling back to the raw value if it won't parse. */
export function When({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-xs text-muted-foreground">—</span>;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return <span className="text-xs">{iso}</span>;

  const seconds = (Date.now() - date.getTime()) / 1000;
  const label =
    seconds < 60
      ? "just now"
      : seconds < 3600
        ? `${Math.floor(seconds / 60)}m ago`
        : seconds < 86_400
          ? `${Math.floor(seconds / 3600)}h ago`
          : `${Math.floor(seconds / 86_400)}d ago`;
  return (
    <span className="text-xs text-muted-foreground" title={date.toLocaleString()}>
      {label}
    </span>
  );
}

/**
 * A unified diff with added/removed lines coloured.
 *
 * Deliberately plain text nodes — the diff is content someone else wrote, and
 * rendering any of it as markup would hand a contributor our own console.
 */
